import * as THREE from 'three';
import { MultisetClient, XRSessionManager } from '@multisetai/vps/core';
import { ThreeAdapter } from '@multisetai/vps/three';
import type { ILocalizeAndMapDetails } from '@multisetai/vps/core';
import { LocalizationState, type NavMeConfig } from '../types';
import { edgeFunctionUrl } from './supabaseClient';

/**
 * ALL MultiSet-specific logic lives here (per project rules). The rest of the
 * app talks to NavMeARSession and never imports the SDK directly.
 *
 * SDK: @multisetai/vps v2.3.x
 * - MultisetClient: auth + localization HTTP client
 * - XRSessionManager: WebXR session lifecycle, frame capture, localization
 * - ThreeAdapter: binds the session to a Three.js renderer/scene/camera and
 *   provides worldFromMap on successful localization.
 */

export interface ARSessionCallbacks {
  onState: (state: LocalizationState, detail?: string) => void;
  /** Fired on every successful localization with the map->world matrix. */
  onLocalized: (result: ILocalizeAndMapDetails, worldFromMap: THREE.Matrix4) => void;
  /** Fired every XR frame after camera matrices are synced. */
  onFrame: () => void;
  onSessionStart: () => void;
  onSessionEnd: () => void;
}

export interface ARSessionHandles {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
}

export class NavMeARSession {
  private client: MultisetClient;
  private session: XRSessionManager | null = null;
  private adapter: ThreeAdapter | null = null;
  private cb: ARSessionCallbacks;
  private authorized = false;

  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;

  static isSupported(): Promise<boolean> {
    return XRSessionManager.isSupported();
  }

  private showMesh: boolean;

  constructor(config: NavMeConfig, handles: ARSessionHandles, callbacks: ARSessionCallbacks) {
    this.cb = callbacks;
    this.renderer = handles.renderer;
    this.scene = handles.scene;
    this.camera = handles.camera;
    this.showMesh = config.showLocalizationMesh ?? false;

    // IMultisetClientConfig is a discriminated union on mapType, so each
    // branch must pass a literal 'map' / 'map-set' for TS to narrow it.
    // Brokered mode: the SDK's authorize() POSTs Basic(clientId:secret) to
    // endpoints.authUrl and accepts {token} back — pointing authUrl at the
    // multiset-token broker (clientId = map code) keeps secrets server-side.
    const base = {
      clientId: config.clientId,
      clientSecret: config.clientSecret,
      isRightHanded: true,
      ...(config.brokered
        ? { endpoints: { authUrl: edgeFunctionUrl('multiset-token') } }
        : {}),
    };
    this.client =
      config.mapType === 'map-set'
        ? new MultisetClient({ ...base, mapType: 'map-set', code: config.code })
        : new MultisetClient({ ...base, mapType: 'map', code: config.code });
  }

  /** Authenticate against the MultiSet API. Call once before starting AR. */
  async authorize(): Promise<void> {
    try {
      await this.client.authorize();
      this.authorized = true;
    } catch (err) {
      this.cb.onState(LocalizationState.Error, authErrorMessage(err));
      throw err;
    }
  }

  /**
   * Build the XR session + adapter. Safe to call once after authorize().
   * startSession() must then be invoked directly from a user gesture.
   */
  initialize(overlayRoot: HTMLElement): void {
    if (!this.authorized) throw new Error('authorize() must be called first');

    this.session = new XRSessionManager(
      this.renderer.getContext() as WebGL2RenderingContext,
      {
        client: this.client,
        overlayRoot,
        autoLocalize: true, // localize once as soon as the session starts
        relocalization: true, // re-localize when tracking is lost then recovered
        backgroundLocalization: true, // periodic drift correction
        bgLocalizationInterval: 30,
        confidenceCheck: true,
        confidenceThreshold: 0.5,
        referenceSpaceType: 'local',
        onSessionStart: () => {
          // XR compositor owns the display; hide the (frozen) canvas.
          this.renderer.domElement.style.display = 'none';
          this.cb.onState(LocalizationState.Localizing);
          this.cb.onSessionStart();
        },
        onSessionEnd: () => {
          this.renderer.domElement.style.display = 'block';
          this.cb.onState(LocalizationState.NotStarted);
          this.cb.onSessionEnd();
        },
        onLocalizationInit: () => this.cb.onState(LocalizationState.Localizing),
        onLocalizationFailure: (reason?: string) =>
          this.cb.onState(LocalizationState.Relocalizing, reason ?? 'Low confidence — retrying'),
        onError: (err: unknown) =>
          this.cb.onState(LocalizationState.Error, errorMessage(err)),
        onContextLost: () =>
          this.cb.onState(LocalizationState.Error, 'Graphics context lost — restart AR'),
      },
    );

    this.adapter = new ThreeAdapter({
      session: this.session,
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      showMesh: this.showMesh, // opt-in via SetupScreen — see NavMeConfig.showLocalizationMesh
      showGizmo: false,
      useDefaultButton: false, // NavMe drives the session from its own UI
      onXRFrame: () => this.cb.onFrame(),
      onLocalizationSuccess: (result, worldFromMap) => {
        this.cb.onState(LocalizationState.Localized);
        this.cb.onLocalized(result, worldFromMap);
      },
    });
    this.adapter.initialize();
  }

  /** MUST be the first call inside a click/tap handler (no awaits before it). */
  startSession(): Promise<void> {
    if (!this.adapter) throw new Error('initialize() must be called first');
    return this.adapter.startSession();
  }

  stopSession(): void {
    this.adapter?.stopSession();
  }

  isActive(): boolean {
    return this.adapter?.isActive() ?? false;
  }

  /** Manual "Relocalize" button — capture and localize one frame now. */
  async relocalize(): Promise<void> {
    if (!this.adapter?.isActive()) return;
    this.cb.onState(LocalizationState.Relocalizing);
    const result = await this.adapter.localizeFrame();
    if (!result) {
      this.cb.onState(LocalizationState.TrackingWeak, 'Relocalization failed — try again');
    }
    // Success path flows through onLocalizationSuccess automatically.
  }

  dispose(): void {
    this.adapter?.dispose();
    this.session?.dispose();
    this.adapter = null;
    this.session = null;
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function authErrorMessage(err: unknown): string {
  const msg = errorMessage(err);
  if (/401|unauthor/i.test(msg)) return 'Invalid Client ID or Secret (401)';
  if (/cors|fetch/i.test(msg))
    return 'Request blocked — is this origin whitelisted in the MultiSet dashboard (Credentials → Settings → Domains)?';
  return msg;
}
